import React from 'react';

import {
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import clsx from 'clsx';
import LoadingSpinner from 'components/LoadingSpinner';
import Tooltip from 'components/Tooltip';
import Checkbox from 'components/V2/Checkbox';
import PropTypes from 'prop-types';

import classes from './listingsTable.module.css';

import { ReactComponent as DeleteIcon } from 'asset/IconsPack/at_trash-03.svg';

import SelectionInfoRow from './SelectionInfoRow';

const ListingsTable = ({
    totalListingsCount,
    header,
    rows,
    onDelete,
    isFetching,
    showCheckbox = false,
    selectedListings = [],
    isSelectAll,
    handleSelectAll,
    handleToggleItemSelection,
    handleClearSelection,
    onChange,
    isChecked,
    disableSelection,
    noDataMessage = 'No listings found',
}) => {
    const HIDDEN_ROW_KEYS = new Set(['id', 'enabled']);

    return (
        <TableContainer className={classes.container}>
            <Table stickyHeader>
                <TableHead className={classes.tableHead}>
                    {showCheckbox && (
                        <SelectionInfoRow
                            isChecked={isChecked}
                            selectedCount={selectedListings.length}
                            rowsCount={rows.length}
                            totalListingsCount={totalListingsCount}
                            isSelectAll={isSelectAll}
                            onSelectAll={handleSelectAll}
                            onClear={handleClearSelection}
                        />
                    )}
                    <TableRow>
                        {showCheckbox && (
                            <TableCell className={classes.tableHeadCell}>
                                <Checkbox
                                    className={classes.checkbox}
                                    checked={isChecked}
                                    onChange={onChange}
                                    disabled={disableSelection}
                                />
                            </TableCell>
                        )}
                        {header.map((item) => (
                            <TableCell key={item.id} className={classes.tableHeadCell}>
                                {item?.label}
                            </TableCell>
                        ))}
                        {onDelete && (
                            <TableCell className={classes.tableHeadCell}>Actions</TableCell>
                        )}
                    </TableRow>
                </TableHead>

                <TableBody>
                    {rows.length === 0 && (
                        <TableRow>
                            <TableCell
                                colSpan={4}
                                className={clsx(classes.tableBodyCell, classes.noDataCell)}
                                align="center"
                            >
                                {noDataMessage}
                            </TableCell>
                        </TableRow>
                    )}
                    {rows.map((row, index) => {
                        const isSelected =
                            selectedListings.find((item) => item.id === row.id) || false;

                        const rowClassName = clsx(classes.tableBodyRow, {
                            [classes.disabledTableRow]: !row.enabled,
                        });

                        const rowEntries = Object.entries(row).filter(
                            ([key]) => !HIDDEN_ROW_KEYS.has(key.toLowerCase())
                        );

                        return (
                            <TableRow key={row.id} className={rowClassName}>
                                {showCheckbox && (
                                    <TableCell className={classes.tableBodyCell}>
                                        <Tooltip
                                            title={
                                                disableSelection && !isSelected
                                                    ? "You can manually select up to 500 listing's only."
                                                    : ''
                                            }
                                            arrow
                                            placement="right"
                                        >
                                            <span>
                                                <Checkbox
                                                    checked={isSelected}
                                                    onChange={() => handleToggleItemSelection(row)}
                                                    disableRipple
                                                    disableFocusRipple
                                                    disabled={disableSelection && !isSelected}
                                                />
                                            </span>
                                        </Tooltip>
                                    </TableCell>
                                )}

                                {rowEntries.map(([key, item], index) => (
                                    <TableCell
                                        key={key}
                                        className={
                                            header[index]?.label === 'Listing Name'
                                                ? clsx(classes.tableBodyCell, classes.strongFont)
                                                : classes.tableBodyCell
                                        }
                                    >
                                        {item}
                                    </TableCell>
                                ))}
                                {onDelete && (
                                    <TableCell
                                        className={classes.tableBodyCell}
                                        title="Remove Listing"
                                    >
                                        <IconButton
                                            onClick={() => onDelete(row)}
                                            className={classes.deleteIconButton}
                                        >
                                            <DeleteIcon className={classes.deleteIcon} />
                                        </IconButton>
                                    </TableCell>
                                )}
                            </TableRow>
                        );
                    })}
                    {isFetching && (
                        <TableRow className={classes.tableBodyRow}>
                            <TableCell
                                colSpan={showCheckbox ? 4 : 3}
                                className={classes.tableBodyCell}
                            >
                                <LoadingSpinner />
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

ListingsTable.propTypes = {
    header: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number,
            label: PropTypes.string,
        })
    ),
    rows: PropTypes.array,
    onDelete: PropTypes.func,
    isFetching: PropTypes.bool,
    showCheckbox: PropTypes.bool,
    totalListingsCount: PropTypes.number,
    selectedListings: PropTypes.array,
    isSelectAll: PropTypes.bool,
    handleSelectAll: PropTypes.func,
    onChange: PropTypes.func,
    handleToggleItemSelection: PropTypes.func,
    handleClearSelection: PropTypes.func,
    isChecked: PropTypes.bool,
    disableSelection: PropTypes.bool,
    noDataMessage: PropTypes.string,
};

export default ListingsTable;
